import type {
  MergeDecisionDto,
  MergeRebaseResponse,
  ReopenedCellRef,
} from '../api/mergeResolution.types';

import type { CellKey, RebaseApplied } from './types';
import { cellKey } from './cellKey';

export function applyRebase(res: MergeRebaseResponse): RebaseApplied {
  // Build decisions from res.Decisions only
  const decisions: Record<CellKey, MergeDecisionDto> = {};
  for (const decision of res.Decisions) {
    const key = cellKey(decision.input_row_id, decision.date_id);
    decisions[key] = decision;
  }

  // Collect reopened and autoRebased cells. A REOPENED cell flagged
  // NoDecisionNeeded asks no question, so it is kept apart from `reopened`.
  const reopened: CellKey[] = [];
  const noDecisionNeeded: CellKey[] = [];
  const autoRebased: CellKey[] = [];
  // Every REOPENED cell, flagged or not, in response order: the carrier when
  // the response has no echo (a mock, or a request sent without Reopened).
  const derivedCarrier: ReopenedCellRef[] = [];

  for (const row of res.Rows) {
    for (const cell of row.Cells) {
      const key = cellKey(row.Input_RowID, cell.DateID);
      if (cell.Outcome === 'REOPENED') {
        derivedCarrier.push({
          Input_RowID: row.Input_RowID,
          DateID: cell.DateID,
        });
        if (cell.NoDecisionNeeded === true) {
          noDecisionNeeded.push(key);
        } else {
          reopened.push(key);
        }
      } else if (cell.Outcome === 'AUTO_REBASED') {
        autoRebased.push(key);
      }
    }
  }

  return {
    decisions,
    reopened,
    autoRebased,
    // The server's echo is sent back exactly as it came, never rebuilt.
    carrier: res.Reopened ?? derivedCarrier,
    noDecisionNeeded,
    session: {
      currentSubmissionId: res.NewCurrentSubmissionID,
      previousCurrentSubmissionId: res.PreviousCurrentSubmissionID,
    },
    serverCounts: res.Counts,
  };
}

/**
 * The decisions to send to the server: every decision except one naming a
 * NoDecisionNeeded cell, which every merge route refuses (400
 * INVALID_DECISION).
 */
export function sendableDecisions(
  decisions: Record<CellKey, MergeDecisionDto>,
  noDecisionNeeded: CellKey[]
): MergeDecisionDto[] {
  if (noDecisionNeeded.length === 0) {
    return Object.values(decisions);
  }
  const skip = new Set(noDecisionNeeded);
  return Object.values(decisions).filter(
    (d) => !skip.has(cellKey(d.input_row_id, d.date_id))
  );
}
